import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, UploadCloud, ChevronDown } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/useAuth";
import { useNotify } from "@/hooks/useNotify";
import {
  SUPPLIER_DOCUMENT_TYPE_GROUPS,
  SUPPLIER_DOCUMENT_TYPE_LABELS,
  COMPLIANCE_DOCUMENT_TYPES,
  ACCEPTED_MIME_TYPES,
  ACCEPTED_FILE_ACCEPT,
  MAX_FILE_SIZE_BYTES,
  sanitizeFileName,
  type SupplierDocumentType,
} from "./supplierDocumentTypes";

const BUCKET = "supplier-documents";

export interface SupplierDocumentRecord {
  id: string;
  document_type: SupplierDocumentType;
  issue_date: string | null;
  expiry_date: string | null;
  notes: string | null;
  storage_path: string;
  file_name: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  supplierId: string;
  /** When provided, dialog edits this document instead of uploading new */
  editing?: SupplierDocumentRecord | null;
  /** Replace just the underlying file for an existing document */
  replacingFor?: SupplierDocumentRecord | null;
  onSuccess: () => void;
}

const SupplierDocumentUploadDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  supplierId,
  editing,
  replacingFor,
  onSuccess,
}) => {
  const { user, profile } = useAuth();
  const notify = useNotify();
  const companyId = profile?.company_id;

  const mode: "upload" | "edit" | "replace" = replacingFor
    ? "replace"
    : editing
    ? "edit"
    : "upload";

  const [file, setFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState<SupplierDocumentType | "">(
    mode === "upload" ? "other" : ""
  );
  const [issueDate, setIssueDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setDocumentType(editing.document_type);
      setIssueDate(editing.issue_date ?? "");
      setExpiryDate(editing.expiry_date ?? "");
      setNotes(editing.notes ?? "");
      setFile(null);
      setDetailsOpen(true);
    } else if (replacingFor) {
      setFile(null);
      setDetailsOpen(false);
    } else {
      setFile(null);
      setDocumentType("other");
      setIssueDate("");
      setExpiryDate("");
      setNotes("");
      setDetailsOpen(false);
    }
  }, [open, editing, replacingFor]);

  // Auto-expand optional details when user selects a compliance type
  useEffect(() => {
    if (
      mode === "upload" &&
      documentType &&
      COMPLIANCE_DOCUMENT_TYPES.includes(documentType as SupplierDocumentType)
    ) {
      setDetailsOpen((prev) => prev || true);
    }
  }, [documentType, mode]);

  const handleFile = (f: File | null) => {
    if (!f) {
      setFile(null);
      return;
    }
    if (!ACCEPTED_MIME_TYPES.includes(f.type)) {
      notify.error("File type not allowed. Use PDF, JPG, PNG or WEBP.");
      return;
    }
    if (f.size > MAX_FILE_SIZE_BYTES) {
      notify.error("File is larger than 20 MB.");
      return;
    }
    setFile(f);
  };

  const handleSubmit = async () => {
    if (!user || !companyId) {
      notify.error("You must be logged in.");
      return;
    }

    setSubmitting(true);
    try {
      if (mode === "edit" && editing) {
        const { error } = await supabase
          .from("supplier_documents")
          .update({
            document_type: documentType as SupplierDocumentType,
            issue_date: issueDate || null,
            expiry_date: expiryDate || null,
            notes: notes || null,
          })
          .eq("id", editing.id)
          .select()
          .maybeSingle();
        if (error) throw error;
        notify.success("Document updated");
        onSuccess();
        onOpenChange(false);
        return;
      }

      if (mode === "replace" && replacingFor) {
        if (!file) {
          notify.error("Choose a file to upload.");
          return;
        }
        const path = `${companyId}/${supplierId}/${crypto.randomUUID()}-${sanitizeFileName(file.name)}`;
        const { error: upErr } = await supabase.storage
          .from(BUCKET)
          .upload(path, file, { contentType: file.type, upsert: false });
        if (upErr) throw upErr;
        const { error } = await supabase
          .from("supplier_documents")
          .update({
            file_name: file.name,
            storage_path: path,
            file_size_bytes: file.size,
            mime_type: file.type,
          })
          .eq("id", replacingFor.id)
          .select()
          .maybeSingle();
        if (error) {
          await supabase.storage.from(BUCKET).remove([path]);
          throw error;
        }
        // best-effort remove old file
        await supabase.storage.from(BUCKET).remove([replacingFor.storage_path]);
        notify.success("File replaced");
        onSuccess();
        onOpenChange(false);
        return;
      }

      // Upload new
      if (!documentType) {
        notify.error("Choose a document type.");
        return;
      }
      if (!file) {
        notify.error("Choose a file to upload.");
        return;
      }
      const path = `${companyId}/${supplierId}/${crypto.randomUUID()}-${sanitizeFileName(file.name)}`;
      const { error: upErr } = await supabase.storage
        .from(BUCKET)
        .upload(path, file, { contentType: file.type, upsert: false });
      if (upErr) throw upErr;

      const { error } = await supabase.from("supplier_documents").insert({
        supplier_id: supplierId,
        company_id: companyId,
        uploaded_by: user.id,
        document_type: documentType,
        file_name: file.name,
        storage_path: path,
        file_size_bytes: file.size,
        mime_type: file.type,
        issue_date: issueDate || null,
        expiry_date: expiryDate || null,
        notes: notes || null,
      });
      if (error) {
        await supabase.storage.from(BUCKET).remove([path]);
        throw error;
      }
      notify.success("Document uploaded");
      onSuccess();
      onOpenChange(false);
    } catch (e: any) {
      notify.error(e?.message ?? "Upload failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {mode === "edit"
              ? "Edit document details"
              : mode === "replace"
              ? "Replace file"
              : "Upload document"}
          </DialogTitle>
          <DialogDescription>
            {mode === "replace"
              ? "Choose a new file. The existing one will be removed."
              : mode === "edit"
              ? "Update the details for this document."
              : "PDF, JPG, PNG or WEBP, up to 20 MB. Only a file is required — everything else is optional."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {mode !== "replace" && (
            <div className="space-y-2">
              <Label>Document type</Label>
              <Select
                value={documentType}
                onValueChange={(v) => setDocumentType(v as SupplierDocumentType)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a type" />
                </SelectTrigger>
                <SelectContent>
                  {SUPPLIER_DOCUMENT_TYPE_GROUPS.map((group) => (
                    <SelectGroup key={group.label}>
                      <SelectLabel>{group.label}</SelectLabel>
                      {group.types.map((t) => (
                        <SelectItem key={t} value={t}>
                          {SUPPLIER_DOCUMENT_TYPE_LABELS[t]}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {mode !== "edit" && (
            <div className="space-y-2">
              <Label>File</Label>
              <label className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground cursor-pointer hover:bg-muted/40">
                <UploadCloud className="h-6 w-6" />
                {file ? (
                  <span className="text-foreground font-medium">{file.name}</span>
                ) : (
                  <span>Click to choose a file</span>
                )}
                <input
                  type="file"
                  className="hidden"
                  accept={ACCEPTED_FILE_ACCEPT}
                  onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
                />
              </label>
            </div>
          )}

          {mode !== "replace" && (
            mode === "edit" ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Issue date</Label>
                    <Input
                      type="date"
                      value={issueDate}
                      onChange={(e) => setIssueDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Expiry date</Label>
                    <Input
                      type="date"
                      value={expiryDate}
                      onChange={(e) => setExpiryDate(e.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Optional notes about this document…"
                    className="min-h-[80px]"
                  />
                </div>
              </>
            ) : (
              <Collapsible open={detailsOpen} onOpenChange={setDetailsOpen}>
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between rounded-md border border-dashed px-3 py-2 text-sm text-muted-foreground hover:bg-muted/40"
                  >
                    <span>Add details (optional)</span>
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${
                        detailsOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-4 pt-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Issue date</Label>
                      <Input
                        type="date"
                        value={issueDate}
                        onChange={(e) => setIssueDate(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Expiry date</Label>
                      <Input
                        type="date"
                        value={expiryDate}
                        onChange={(e) => setExpiryDate(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Optional notes about this document…"
                      className="min-h-[80px]"
                    />
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {mode === "edit" ? "Save" : mode === "replace" ? "Replace" : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SupplierDocumentUploadDialog;
